import { useState } from "react";
import { useForm } from "react-hook-form";
import { Edit2 } from "lucide-react";
import { useTenants, useUpdateTenant } from "@/api/queries";
import type { TenantRead } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";

// Организации заводятся/удаляются ТОЛЬКО в signaris-auth; NET их зеркалит.
// Здесь — read-only identity (slug/name/status из auth) + правка NET VPN-лимитов.
export function TenantsPage() {
  const { data, isLoading } = useTenants();
  const [editTarget, setEditTarget] = useState<TenantRead | null>(null);

  return (
    <div className="space-y-6">
      <header>
        <span className="label">Организации (superadmin)</span>
        <h1 className="font-display text-3xl font-bold mt-2">
          Организации-заказчики
        </h1>
        <p className="text-sm text-fg-muted mt-1">
          Организации управляются в Signaris (едином входе). Здесь — VPN-лимиты.
        </p>
      </header>

      <Card>
        <CardBody className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Slug</TH>
                <TH>Название</TH>
                <TH>Email</TH>
                <TH>Пользователей (лимит)</TH>
                <TH>Статус</TH>
                <TH>Создан</TH>
                <TH className="text-right">VPN-лимиты</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading && (
                <TR>
                  <TD colSpan={7} className="text-center text-fg-subtle py-8">
                    Загрузка…
                  </TD>
                </TR>
              )}
              {data?.items.length === 0 && (
                <TR>
                  <TD colSpan={7} className="text-center text-fg-subtle py-12">
                    Организаций нет.
                  </TD>
                </TR>
              )}
              {data?.items.map((t) => (
                <TR key={t.id}>
                  <TD className="font-mono text-sm">{t.slug}</TD>
                  <TD className="font-medium">
                    {t.name}
                    {t.legacy_vpn_email_format && (
                      <Badge tone="warning" className="ml-2">
                        legacy
                      </Badge>
                    )}
                  </TD>
                  <TD className="text-fg-muted">{t.contact_email ?? "—"}</TD>
                  <TD>{t.max_users}</TD>
                  <TD>
                    <StatusBadge status={t.status} />
                  </TD>
                  <TD className="text-xs text-fg-muted">
                    {new Date(t.created_at).toLocaleDateString("ru")}
                  </TD>
                  <TD className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditTarget(t)}
                      title="Изменить VPN-лимиты"
                    >
                      <Edit2 size={14} />
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      <EditDialog tenant={editTarget} onClose={() => setEditTarget(null)} />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "active") return <Badge tone="success">active</Badge>;
  if (status === "suspended") return <Badge tone="danger">suspended</Badge>;
  return <Badge tone="warning">{status}</Badge>;
}

// Правка только NET VPN-конфига. slug/name/status — из auth, read-only.
function EditDialog({
  tenant,
  onClose,
}: {
  tenant: TenantRead | null;
  onClose: () => void;
}) {
  const { register, handleSubmit, formState } = useForm({
    values: tenant
      ? {
          contact_email: tenant.contact_email ?? "",
          max_users: tenant.max_users,
          max_traffic_gb_month: tenant.max_traffic_gb_month,
        }
      : undefined,
  });
  const update = useUpdateTenant();
  const [err, setErr] = useState<string | null>(null);

  if (!tenant) return null;

  const submit = async (d: {
    contact_email: string;
    max_users: number;
    max_traffic_gb_month: number;
  }) => {
    setErr(null);
    try {
      await update.mutateAsync({
        id: tenant.id,
        body: {
          contact_email: d.contact_email || undefined,
          max_users: Number(d.max_users),
          max_traffic_gb_month: Number(d.max_traffic_gb_month),
        },
      });
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка";
      setErr(msg);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`VPN-лимиты — ${tenant.name}`}
      description="Организация управляется в Signaris; здесь только NET-настройки."
    >
      <form onSubmit={handleSubmit(submit)} className="space-y-4">
        <div>
          <Label htmlFor="e-contact">Контактный email (NET)</Label>
          <Input id="e-contact" type="email" {...register("contact_email")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="e-users">Max users</Label>
            <Input id="e-users" type="number" {...register("max_users")} />
          </div>
          <div>
            <Label htmlFor="e-gb">Max GB/мес</Label>
            <Input id="e-gb" type="number" {...register("max_traffic_gb_month")} />
          </div>
        </div>
        {err && (
          <div className="text-sm text-state-danger bg-state-danger/10 border border-state-danger/20 rounded-lg px-3 py-2">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={formState.isSubmitting}>
            Сохранить
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
